import React, { useEffect, useState } from "react";
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Grid,
    Paper,
    Typography,
    IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { makePUTRequestOnUserService } from "../../Api/api";

const NRI_IS_HOLDING_OPTIONS = [
    { value: "indian", label: "Indian Passport" },
    { value: "foreign", label: "Foreign Passport" },
];

const PROOF_OF_NRI_DOCUMENT_TYPE_OPTIONS = [
    {
        value: "valid_visa",
        label: "Valid VISA (Employment / Residence / Student / Dependent etc.)",
    },
    { value: "work_residence_permit", label: "Work / Residence Permit" },
    { value: "oci_card", label: "OCI (Overseas Citizen of India) Card" },
    { value: "pio_card", label: "PIO (Person of Indian Origin) Card" },
    { value: "pio_declaration", label: "PIO Declaration" },
];

export const ProofOfNRI = ({
    poa,
    currentUser,
    user_type,
    fetchKycDocs,
    getErrorMessage,
}: any) => {
    const toastr = require("toastr");

    const [form, setForm] = useState({
        nri_is_holding: "",
        proof_of_nri_document_type: "",
        proof_of_nri_document_password: "",
    });
    const [file, setFile] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (poa && Object.keys(poa).length > 0) {
            setForm({
                nri_is_holding: poa?.nri_is_holding || "",
                proof_of_nri_document_type: poa?.proof_of_nri_document_type || "",
                proof_of_nri_document_password:
                    poa?.proof_of_nri_document_password || "",
            });
        }
    }, [poa]);

    const handleChange = (e: any) => {
        setForm((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            const fd = new FormData();
            fd.append("user_id", currentUser.uuid);
            fd.append("user_type", user_type);

            if (form.nri_is_holding)
                fd.append("nri_is_holding", form.nri_is_holding);
            if (form.proof_of_nri_document_type)
                fd.append("proof_of_nri_document_type", form.proof_of_nri_document_type);
            fd.append(
                "proof_of_nri_document_password",
                form.proof_of_nri_document_password || ""
            );

            if (file) {
                fd.append(
                    "proof_of_nri_document",
                    file,
                    `${Date.now()}_${file.name}`
                );
            }

            const resp = await makePUTRequestOnUserService(
                `/partneruser/kyc/passport/update_proof_of_nri/`,
                fd
            );

            if (resp.status === 200) {
                setFile(null);
                await fetchKycDocs();
                toastr.success("Proof of NRI updated successfully");
            }
        } catch (err: any) {
            toastr.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const getBase64MimeType = (base64: string) => {
        if (!base64) return "";
        if (base64.startsWith("JVBERi0")) return "application/pdf";
        if (base64.startsWith("iVBOR")) return "image/png";
        if (base64.startsWith("/9j/")) return "image/jpeg";
        return "image/jpeg";
    };

    const base64ToBlob = (base64: string): Blob => {
        const mimeType = getBase64MimeType(base64);
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
        return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    };

    const openBase64InNewTab = (base64: string) => {
        const blob = base64ToBlob(base64);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
    };

    const nriFilename = poa?.proof_of_nri_document || null;
    const nriImageBase64 = poa?.proof_of_nri_document_image || null;
    const nriExtension = poa?.proof_of_nri_document_extension || null;
    const hasExistingDocument = !!(nriFilename || nriImageBase64);

    const isExistingPdf =
        nriExtension === "pdf" ||
        (nriImageBase64 && getBase64MimeType(nriImageBase64) === "application/pdf");

    return (
        <Grid container spacing={4}>
            <Grid item xs={5}>
                <Box
                    sx={{
                        height: 350,
                        border: "1px dashed #ccc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                        background: "#fafafa",
                        overflow: "hidden",
                    }}
                >
                    {file ? (
                        file.type === "application/pdf" ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1,
                                    p: 2,
                                }}
                            >
                                <Typography variant="h6" color="error">
                                    PDF
                                </Typography>
                                <Typography variant="body2">{file.name}</Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        const url = URL.createObjectURL(file);
                                        window.open(url, "_blank");
                                    }}
                                >
                                    View PDF
                                </Button>
                            </Box>
                        ) : (
                            <img
                                src={URL.createObjectURL(file)}
                                alt="preview"
                                style={{ maxWidth: "100%", maxHeight: "100%" }}
                            />
                        )
                    ) : hasExistingDocument &&
                        nriImageBase64 ? (
                        isExistingPdf ? (
                            <Button
                                variant="outlined"
                                onClick={() => openBase64InNewTab(nriImageBase64)}
                            >
                                View PDF
                            </Button>
                        ) : (
                            <img
                                src={`data:image/${nriExtension || "jpeg"};base64,${nriImageBase64}`}
                                alt="NRI Document"
                                style={{ maxWidth: "100%", maxHeight: "100%" }}
                            />
                        )
                    ) : hasExistingDocument ? (
                        <Typography variant="body2" color="text.secondary">
                            Document exists (upload to replace)
                        </Typography>
                    ) : (
                        <Typography variant="body1" color="text.secondary">
                            Upload NRI Document
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                    <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setFile(e.target.files?.[0])}
                        style={{ flex: 1 }}
                    />
                    {file && (
                        <IconButton
                            color="error"
                            size="small"
                            onClick={() => setFile(null)}
                            title="Remove file"
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Box>

                {file && (
                    <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ mt: 1, display: "block" }}
                    >
                        New file selected: {file.name}
                    </Typography>
                )}
            </Grid>

            <Grid item xs={7}>
                <Paper sx={{ p: 3 }}>
                    <h2>Proof of NRI</h2>

                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                        <InputLabel id="nri-holding-label">
                            Passport Type (NRI is holding)
                        </InputLabel>
                        <Select
                            labelId="nri-holding-label"
                            id="nri-holding-select"
                            name="nri_is_holding"
                            value={form.nri_is_holding}
                            onChange={handleChange}
                            label="Passport Type (NRI is holding)"
                        >
                            {NRI_IS_HOLDING_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                        <InputLabel id="nri-doc-type-label">
                            Proof of NRI Document Type
                        </InputLabel>
                        <Select
                            labelId="nri-doc-type-label"
                            id="nri-doc-type-select"
                            name="proof_of_nri_document_type"
                            value={form.proof_of_nri_document_type}
                            onChange={handleChange}
                            label="Proof of NRI Document Type"
                        >
                            {PROOF_OF_NRI_DOCUMENT_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        variant="outlined"
                        name="proof_of_nri_document_password"
                        label="Document Password (if encrypted)"
                        value={form.proof_of_nri_document_password}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                    />

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Updating..." : "Update"}
                    </Button>
                </Paper>
            </Grid>
        </Grid>
    );
};